import { IEntry, Permission, IEntryExported } from '@/types/entry'
import { Dictionary, Maybe } from '@/types/general'
import PCrypto from '@/util/crypto/PCrypto'
import { RootState, RootGetters } from '@/store/types'
import { RouteBuilder } from '@/router'
import EntryRepository from './entry-repository'

type IEntryKey = keyof IEntry

type IEntryObject = {
    [key in IEntryKey]?: any
}

/**
 * Entry model
 *
 * See entry-repository.ts for usage
 */
export default class Entry implements IEntry {
    public entryId?: string
    public schemaType!: string
    public parentId?: string
    public sortOrder!: number
    public tags!: string[]
    public icon!: string
    public content!: string
    public createdBy!: string
    public createdAt!: Date
    public updatedAt!: Date
    public viewedAt!: Date
    public title!: string
    public pendingUpdate!: boolean
    public documentKey!: string
    public permission!: Permission
    public deletedAt?: Date

    private $state?: RootState
    private $getters?: RootGetters

    /**
     * Cached fields
     */
    private _fields?: Dictionary<string>

    /**
     * Create a new Entry object manually
     */
    public constructor(id?: string) {
        this.id = id
    }

    /**
     * Set entry ID
     */
    public set id(id: Maybe<string>) {
        this.entryId = id
    }

    /**
     * Get entry ID
     */
    public get id(): Maybe<string> {
        return this.entryId
    }

    /**
     * Get the decrypted content fields
     */
    public get fields(): Dictionary<string> {
        if (this._fields === undefined) {
            if (!this.synced()) {
                this._fields = {}
            } else {
                this._fields = JSON.parse(PCrypto.AES.decrypt(this.content, this.key))
            }
        }

        return this._fields!
    }

    /**
     * Merge fields into existing fields
     */
    public setFields(fields: Dictionary<string>): Entry {
        this._fields = { ...this.fields, ...fields }
        return this
    }

    /**
     * Get the subtitle for the entry
     *
     * Tries to pick an important field to show,
     * if no important ones are found it will show
     * the schema type
     */
    public get subtitle(): string {
        if (this.isVault()) {
            return `${this.children.length} items`
        }
        if (this.loaded()) {
            return this.fields.username
                || this.fields.domain
                || this.fields.hostname
                || this.schemaType
        }
        return this.schemaType
    }

    /**
     * Check if the entry is a vault
     */
    public isVault(): boolean {
        return this.schemaType === 'vault'
    }

    /**
     * Check if the entry is a login
     */
    public isLogin(): boolean {
        return !this.isVault
    }

    /**
     * Check if the entry is already synced
     * to the API
     */
    public synced(): boolean {
        return this.id !== undefined
    }

    /**
     * Check if the entry has been fully
     * loaded from the API
     */
    public loaded(): boolean {
        return !!this.content
    }

    /**
     * Check if the entry is at the topmost level
     * (no parent)
     */
    public isTopLevel(): boolean {
        return this.isVault() && !this.parentId
    }

    /**
     * Check if the entry is deleted
     */
    public deleted(): boolean {
        return !!this.deletedAt
    }

    /**
     * Get the direct children of this entry
     */
    public get children(): Entry[] {
        if (!this.isConnected()) {
            throw Error('Model not connected when accessing field: children')
        }

        if (this.isLogin()) {
            throw Error('Tried to access children of a login')
        }

        const { allEntries } = this.$state!.entries
        return Object.values(allEntries)
            .filter(entry => entry.parentId === this.id)
    }

    /**
     * Get all descendants of this entry
     */
    public get descendants(): Entry[] {
        if (!this.isConnected()) {
            throw Error('Model not connected when accessing field: descendants')
        }

        if (this.isLogin()) {
            throw Error('Tried to access descendants of a login')
        }

        return [
            ...this.children,
            ...this.children.reduce(
                (grandchildren, child) => [...grandchildren, ...child.descendants],
                [] as Entry[]
            )
        ]
    }

    /**
     * Get the direct parent of this entry
     */
    public get parent(): Maybe<Entry> {
        if (!this.isConnected()) {
            throw Error('Model not connected when accessing field: parent')
        }

        if (this.isTopLevel()) {
            return undefined
        }

        const { getEntry } = this.$getters!
        return getEntry(this.parentId!)
    }

    /**
     * Get all ancestors of this entry,
     * ordered from top-most to lowest
     */
    public get ancestors(): Entry[] {
        if (!this.isConnected()) {
            throw Error('Model not connected when accessing field: ancestors')
        }

        const { parent } = this
        return parent ? [...parent.ancestors, parent] : []
    }

    /**
     * Get IDs of ancestors, INCLUDING this entry
     */
    public get path(): string[] {
        if (!this.isConnected()) {
            throw Error('Model not connected when accessing field: titlePath')
        }

        return [...this.ancestors.map(entry => entry.id!), this.id!]
    }

    /**
     * Get all titles of the vaults this
     * entry is contained in, INCLUDING this entry title
     */
    public get titlePath(): string[] {
        if (!this.isConnected()) {
            throw Error('Model not connected when accessing field: titlePath')
        }

        return [...this.ancestors.map(entry => entry.title), this.title]
    }

    /**
     * Get the links of the vaults this
     * entry is contained in, INCLUDING this entry link
     */
    public get linkPath(): string[] {
        if (!this.isConnected()) {
            throw Error('Model not connected when accessing field: linkPath')
        }

        return [...this.ancestors.map(entry => entry.link), this.link]
    }

    /**
     * Get the URL path of the current entry
     */
    public get link(): string {
        return RouteBuilder.buildEditRoute(this.id)
    }

    /**
     * Save a reference to the Vuex state and getters
     * to be used in helper methods
     */
    public connect(state: RootState, getters: RootGetters): Entry {
        this.$state = state
        this.$getters = getters
        return this
    }

    /**
     * Check whether this entry has been connected
     * to the Vuex store
     */
    private isConnected(): boolean {
        return this.$state !== undefined && this.$getters !== undefined
    }

    /**
     * Get the decrypred document key
     */
    private get key(): string {
        return PCrypto.RSA.decrypt(this.documentKey)
    }

    /**
     * Get the content encrypted
     *
     * Will not throw an error if the content
     * isn't loaded yet
     */
    private get encryptedContent(): string {
        // can get the content if the entry is
        // fully loaded from API data, OR
        // the entry is brand new
        if (this.loaded() || !this.synced()) {
            return PCrypto.AES.encrypt(JSON.stringify(this.fields), this.key)
        }
        return ''
    }

    /**
     * Get the entry as an object to be saved
     * in the IDB / API
     *
     * Need to explicitly set keys to not clutter
     * the IDB
     */
    public asObject(): IEntry {
        return {
            entryId: this.id,
            schemaType: this.schemaType,
            title: this.title,
            parentId: this.parentId,
            sortOrder: this.sortOrder,
            tags: this.tags,
            icon: this.icon,
            documentKey: this.documentKey,
            permission: this.permission,
            content: this.encryptedContent,
            createdBy: this.createdBy,
            createdAt: this.createdAt,
            updatedAt: this.updatedAt,
            viewedAt: this.viewedAt,
            deletedAt: this.deletedAt,
            pendingUpdate: this.pendingUpdate
        }
    }

    /**
     * Get an unencrypted object of the entry to
     * be serialized in export, including all
     * children serialized the same way
     */
    public async asExportedObject(): Promise<IEntryExported> {
        const rawChildren = await EntryRepository.fetchChildren(this.id!)
        const children = await Promise.all(
            rawChildren.map(child => child.asExportedObject())
        )

        // remove title from fields
        const fields = { ...this.fields }
        delete fields.title

        return {
            title: this.title,
            schemaType: this.schemaType,
            tags: this.tags,
            icon: this.icon,
            fields,
            children
        }
    }

    /**
     * Set entry data from an object
     */
    public setData(data: IEntryObject): Entry {
        (Object.keys(data) as IEntryKey[]).forEach((key: IEntryKey) => {
            (this as IEntryObject)[key] = data[key]
        })

        return this
    }

    /**
     * Persist changes to IDB and API
     */
    public async save(): Promise<Entry> {
        return EntryRepository.save(this)
    }
}
